/**
 * DriverEquipmentAssignModal - Assign fuel card, toll badge, or parking badge to a driver
 *
 * Two modes:
 * 1. Assign existing unassigned equipment from the org
 * 2. Create new equipment and assign to driver
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, X, CreditCard, CircleDot, ParkingCircle, Plus } from 'lucide-react';
import { fuelCardAPI, driverBadgeAPI } from '../../../api/index.js';

// `labelKey` → t('drivers.equipment.*')
const EQUIPMENT_TYPES = [
  { value: 'fuel_card', labelKey: 'drivers.equipment.fuelCard', icon: CreditCard, color: 'blue' },
  { value: 'toll', labelKey: 'drivers.equipment.tollBadge', icon: CircleDot, color: 'amber' },
  { value: 'parking', labelKey: 'drivers.equipment.parkingBadge', icon: ParkingCircle, color: 'purple' },
];

// Provider brand names are proper nouns — not translated. `labelKey` marks the few generic entries.
const FUEL_CARD_PROVIDERS = [
  { value: 'totalenergies', label: 'TotalEnergies' },
  { value: 'dkv', label: 'DKV' },
  { value: 'shell', label: 'Shell' },
  { value: 'uta_edenred', label: 'UTA/Edenred' },
  { value: 'esso_wex', label: 'Esso/WEX' },
  { value: 'leclerc_pro', label: 'Leclerc Pro' },
  { value: 'other', labelKey: 'drivers.equipmentModal.providerOther' },
];

const TOLL_PROVIDERS = [
  { value: 'vinci', label: 'Vinci Autoroutes' },
  { value: 'aprr', label: 'APRR' },
  { value: 'sanef', label: 'Sanef' },
  { value: 'area', label: 'AREA' },
  { value: 'bipandgo', label: 'Bip&Go' },
  { value: 'ulys', label: 'Ulys' },
  { value: 'other', labelKey: 'drivers.equipmentModal.providerOther' },
];

const PARKING_PROVIDERS = [
  { value: 'indigo', label: 'Indigo' },
  { value: 'qpark', label: 'Q-Park' },
  { value: 'effia', label: 'Effia' },
  { value: 'saemes', label: 'Saemes' },
  { value: 'zenpark', label: 'Zenpark' },
  { value: 'site_badge', labelKey: 'drivers.equipmentModal.providerSiteBadge' },
  { value: 'other', labelKey: 'drivers.equipmentModal.providerOther' },
];

function DriverEquipmentAssignModal({ isOpen, onClose, onSuccess, onMessage, driver }) {
  const { t, i18n } = useTranslation();
  const [equipmentType, setEquipmentType] = useState('fuel_card');
  const [mode, setMode] = useState('existing'); // 'existing' or 'new'
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // New equipment form fields
  const [newNumber, setNewNumber] = useState('');
  const [newProvider, setNewProvider] = useState('');
  const [newProviderName, setNewProviderName] = useState('');
  const [newExpiry, setNewExpiry] = useState('');
  const [newLimit, setNewLimit] = useState('');
  const [newNotes, setNewNotes] = useState('');

  // Load available unassigned items when type changes
  useEffect(() => {
    if (!isOpen) return;
    loadAvailableItems();
  }, [isOpen, equipmentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAvailableItems = async () => {
    setLoadingItems(true);
    setSelectedItemId('');
    try {
      if (equipmentType === 'fuel_card') {
        const res = await fuelCardAPI.getAll();
        // Show unassigned cards (no driver_id) or pool cards
        setAvailableItems((res.data || []).filter(c => !c.driver_id && c.status === 'active'));
      } else {
        const res = await driverBadgeAPI.getAll({ badge_type: equipmentType });
        setAvailableItems((res.data || []).filter(b => !b.driver_id && b.status === 'active'));
      }
    } catch {
      setAvailableItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const resetNewForm = () => {
    setNewNumber('');
    setNewProvider('');
    setNewProviderName('');
    setNewExpiry('');
    setNewLimit('');
    setNewNotes('');
  };

  useEffect(() => {
    if (isOpen) {
      setMode('existing');
      resetNewForm();
    }
  }, [isOpen]);

  const getProviderList = () => {
    if (equipmentType === 'fuel_card') return FUEL_CARD_PROVIDERS;
    if (equipmentType === 'toll') return TOLL_PROVIDERS;
    return PARKING_PROVIDERS;
  };

  const handleAssignExisting = async () => {
    if (!selectedItemId) return;
    setLoading(true);
    try {
      if (equipmentType === 'fuel_card') {
        await fuelCardAPI.update(selectedItemId, { driver_id: driver.id, card_type: 'driver' });
      } else {
        await driverBadgeAPI.update(selectedItemId, { driver_id: driver.id, assignment_type: 'driver' });
      }
      onMessage?.({ title: t('drivers.messages.successTitle'), message: t('drivers.equipmentModal.assignSuccess'), type: 'success' });
      onSuccess?.();
    } catch (error) {
      console.error('Error assigning equipment:', error);
      onMessage?.({ title: t('drivers.messages.errorTitle'), message: t('drivers.equipmentModal.assignError'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = async () => {
    if (!newNumber || !newProvider) return;
    setLoading(true);
    try {
      if (equipmentType === 'fuel_card') {
        await fuelCardAPI.create({
          card_number: newNumber,
          provider: newProvider,
          provider_name: newProvider === 'other' ? newProviderName : null,
          driver_id: driver.id,
          card_type: 'driver',
          expiry_date: newExpiry || null,
          monthly_limit: newLimit ? parseFloat(newLimit) : null,
          notes: newNotes || null,
        });
      } else {
        await driverBadgeAPI.create({
          badge_number: newNumber,
          badge_type: equipmentType,
          provider: newProvider,
          provider_name: newProvider === 'other' || newProvider === 'site_badge' ? newProviderName : null,
          driver_id: driver.id,
          assignment_type: 'driver',
          expiry_date: newExpiry || null,
          monthly_limit: newLimit ? parseFloat(newLimit) : null,
          notes: newNotes || null,
        });
      }
      onMessage?.({ title: t('drivers.messages.successTitle'), message: t('drivers.equipmentModal.createSuccess'), type: 'success' });
      onSuccess?.();
    } catch (error) {
      console.error('Error creating equipment:', error);
      onMessage?.({ title: t('drivers.messages.errorTitle'), message: t('drivers.equipmentModal.createError'), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === 'existing') {
      handleAssignExisting();
    } else {
      handleCreateNew();
    }
  };

  if (!isOpen) return null;

  const typeConfig = EQUIPMENT_TYPES.find(et => et.value === equipmentType) || EQUIPMENT_TYPES[0];
  const TypeIcon = typeConfig.icon;
  const isFuelCard = equipmentType === 'fuel_card';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-xl flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            <div>
              <h3 className="font-semibold">{t('drivers.equipmentModal.title')}</h3>
              <p className="text-xs text-blue-200">{driver?.first_name} {driver?.last_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-blue-500 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Equipment Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('drivers.equipmentModal.equipmentType')}</label>
            <div className="grid grid-cols-3 gap-2">
              {EQUIPMENT_TYPES.map(({ value, labelKey, icon: Icon, color }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => { setEquipmentType(value); setMode('existing'); resetNewForm(); }}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    equipmentType === value
                      ? `border-${color}-500 bg-${color}-50 text-${color}-700`
                      : 'border-gray-200 hover:border-gray-300 text-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">{t(labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setMode('existing')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                mode === 'existing' ? 'bg-white shadow text-gray-900' : 'text-gray-500'
              }`}
            >
              {t('drivers.equipmentModal.modeExisting')}
            </button>
            <button
              type="button"
              onClick={() => setMode('new')}
              className={`flex-1 py-2 text-sm font-medium rounded-md transition-all flex items-center justify-center gap-1 ${
                mode === 'new' ? 'bg-white shadow text-gray-900' : 'text-gray-500'
              }`}
            >
              <Plus className="w-4 h-4" /> {t('drivers.equipmentModal.modeNew')}
            </button>
          </div>

          {mode === 'existing' ? (
            /* Existing equipment selector */
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('drivers.equipmentModal.availableEquipment')}
              </label>
              {loadingItems ? (
                <p className="text-sm text-gray-500 py-2">{t('common.states.loading')}</p>
              ) : availableItems.length === 0 ? (
                <div className="text-sm text-gray-500 py-3 text-center bg-gray-50 rounded-lg">
                  {t('drivers.equipmentModal.noneAvailable')}
                  <button
                    type="button"
                    onClick={() => setMode('new')}
                    className="text-blue-600 hover:underline ml-1"
                  >
                    {t('drivers.equipmentModal.createOne')}
                  </button>
                </div>
              ) : (
                <select
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">{t('common.actions.selectPlaceholder')}</option>
                  {availableItems.map(item => {
                    const num = isFuelCard ? item.card_number : item.badge_number;
                    return (
                      <option key={item.id} value={item.id}>
                        {item.provider} — {num}
                        {item.expiry_date
                          ? ` ${t('drivers.equipmentModal.expirySuffix', { date: new Date(item.expiry_date).toLocaleDateString(i18n.language) })}`
                          : ''}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          ) : (
            /* New equipment form */
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isFuelCard ? t('drivers.equipmentModal.cardNumber') : t('drivers.equipmentModal.badgeNumber')} *
                </label>
                <input
                  type="text"
                  value={newNumber}
                  onChange={(e) => setNewNumber(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={isFuelCard ? t('drivers.equipmentModal.cardNumberPlaceholder') : t('drivers.equipmentModal.badgeNumberPlaceholder')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('drivers.equipmentModal.provider')} *</label>
                <select
                  value={newProvider}
                  onChange={(e) => setNewProvider(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">{t('common.actions.selectPlaceholder')}</option>
                  {getProviderList().map(p => (
                    <option key={p.value} value={p.value}>{p.labelKey ? t(p.labelKey) : p.label}</option>
                  ))}
                </select>
              </div>

              {(newProvider === 'other' || newProvider === 'site_badge') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('drivers.equipmentModal.providerName')}</label>
                  <input
                    type="text"
                    value={newProviderName}
                    onChange={(e) => setNewProviderName(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('drivers.equipmentModal.providerNamePlaceholder')}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('drivers.equipmentModal.expiryDate')}</label>
                  <input
                    type="date"
                    value={newExpiry}
                    onChange={(e) => setNewExpiry(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('drivers.equipmentModal.monthlyLimit')}</label>
                  <input
                    type="number"
                    value={newLimit}
                    onChange={(e) => setNewLimit(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="500"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('drivers.equipmentModal.notes')}</label>
                <textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  placeholder={t('drivers.equipmentModal.notesPlaceholder')}
                  maxLength={200}
                />
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || (mode === 'existing' && !selectedItemId) || (mode === 'new' && (!newNumber || !newProvider))}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <TypeIcon className="w-4 h-4" />
              )}
              {mode === 'existing' ? t('drivers.equipmentModal.assign') : t('drivers.equipmentModal.createAndAssign')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DriverEquipmentAssignModal;
